import React from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  fetchAuditLogForRecord,
  type AuditLogRow,
  type RecordHistoryCursor,
  type RecordHistoryFilters,
} from '@/data/auditLog';
import { fetchProfilesByAuthIds } from '@/data/userProfiles';
import type { TransferRecord } from '@/data/transferRecord';
import { sentenceCaseStatus } from '@/domain/warehouse';
import RecordHistoryTab, { type DisplayItem } from '../../../components/RecordHistoryTab';
import { useRoles } from '../../../hooks/useRoles';
import {
  TRANSFER_ACTIVITY_TABLE,
  TRANSFER_HISTORY_TABLES,
  transferHistoryFieldLabel,
  transferHistoryTab,
} from './transferRecordView';

const ACTION_OPTIONS = ['INSERT', 'UPDATE', 'DELETE'];
const ACTIVITY_FILTER = '__activity';
const EXTRA_FILTERS = [{ value: ACTIVITY_FILTER, label: 'Activity' }];
const NO_UNDO = { mutate: () => undefined, isPending: false };
const EMPTY_FILTERS: RecordHistoryFilters = { search: '', table: '', action: '' };
const TABLE_OPTIONS = [...TRANSFER_HISTORY_TABLES];
const NO_TABLES: { value: string; label: string }[] = [];

/** record_history_page for 'transfer' (audit rows of the transfer, its lines and its stops), paged. */
function useTransferAuditHistory(transferId: string, enabled: boolean) {
  const [filters, setFilters] = React.useState<RecordHistoryFilters>(EMPTY_FILTERS);
  const [search, setSearch] = React.useState('');
  React.useEffect(() => {
    if (!filters.search) {
      setSearch('');
      return undefined;
    }
    const timer = window.setTimeout(() => setSearch(filters.search), 250);
    return () => window.clearTimeout(timer);
  }, [filters.search]);
  // The Activity filter shows the record's own timeline only; the server gets no table for it.
  const table = filters.table === ACTIVITY_FILTER ? '' : filters.table;
  const queryFilters = React.useMemo<RecordHistoryFilters>(
    () => ({ search: filters.search ? search : '', table, action: filters.action }),
    [filters.action, filters.search, search, table],
  );
  const query = useInfiniteQuery({
    queryKey: ['record-audit', 'transfer', transferId, queryFilters],
    enabled: enabled && Boolean(transferId),
    initialPageParam: undefined as RecordHistoryCursor | undefined,
    queryFn: async ({ pageParam }) => {
      const result = await fetchAuditLogForRecord('transfer', transferId, 100, { cursor: pageParam, filters: queryFilters });
      if (result.error) throw result.error;
      return { rows: result.data, nextCursor: result.nextCursor };
    },
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const rows = React.useMemo(() => {
    const unique = new Map<number, AuditLogRow>();
    for (const page of query.data?.pages ?? []) for (const row of page.rows) unique.set(row.id, row);
    return Array.from(unique.values());
  }, [query.data]);
  return { query, rows, setFilters, loading: query.isLoading || search !== filters.search };
}

type TransferHistorySectionProps = {
  record: TransferRecord;
};

/**
 * History: field changes to the transfer, its lines and stops (audit rows), merged with the record's
 * business timeline (confirmations, picks, shipments, receipts, problems, links). There is no Undo
 * here: undo_change finds rows by an `id` column the transfer tables do not have, and reverting a
 * pick or a receipt row by row would go around the Warehouse actions' checks.
 */
export default function TransferHistorySection({ record }: TransferHistorySectionProps) {
  const transferId = record.transfer.id;
  const { canViewHistory } = useRoles();
  const { query, rows, setFilters, loading } = useTransferAuditHistory(transferId, canViewHistory);
  const actorIds = React.useMemo(
    () => Array.from(new Set(rows.map((row) => row.changed_by).filter((id): id is string => Boolean(id)))).sort(),
    [rows],
  );
  const profiles = useQuery({
    queryKey: ['transfer-history-profiles', actorIds],
    enabled: actorIds.length > 0,
    queryFn: async () => {
      const result = await fetchProfilesByAuthIds(actorIds);
      if (result.error) throw result.error;
      return { names: result.data, headshots: result.headshots };
    },
  });

  // The timeline's entries as history rows. Negative ids never collide with audit_log ids.
  const timelineItems = React.useMemo<DisplayItem[]>(() => record.timeline.map((entry, index) => ({
    row: {
      id: -(index + 1),
      table_name: TRANSFER_ACTIVITY_TABLE,
      record_id: entry.id,
      action: sentenceCaseStatus(entry.kind),
      changed_at: entry.at,
      changed_by: entry.actor?.id ?? null,
      old_data: null,
      new_data: null,
      payload_mode: 'full',
      record_label: entry.summary || sentenceCaseStatus(entry.kind),
      activity_source: entry.actor ? 'user' : 'system',
    },
    changes: [],
  })), [record.timeline]);

  const userNames = React.useMemo(() => {
    const names: { [id: string]: string } = { ...(profiles.data?.names ?? {}) };
    for (const entry of record.timeline) {
      if (entry.actor?.id && entry.actor.name) names[entry.actor.id] = entry.actor.name;
    }
    return names;
  }, [profiles.data?.names, record.timeline]);

  const filterActivity = React.useCallback(
    (item: DisplayItem, value: string) => value === ACTIVITY_FILTER && item.row.table_name === TRANSFER_ACTIVITY_TABLE,
    [],
  );
  const renderNoAction = React.useCallback(() => null, []);
  const loadMore = React.useCallback(() => {
    void query.fetchNextPage();
  }, [query]);
  const retry = React.useCallback(() => {
    void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch());
  }, [query]);

  const error = query.error || profiles.error;
  return (
    <div className="transfer-record-panel transfer-record-history">
      <RecordHistoryTab
        title="History"
        auditRows={canViewHistory ? rows : []}
        loading={canViewHistory ? loading : false}
        refreshing={query.isFetching && !query.isFetchingNextPage}
        error={error ? (error as { message?: string }).message || 'History could not be loaded.' : undefined}
        extraDisplayItems={timelineItems}
        extraFilterOptions={EXTRA_FILTERS}
        filterPredicate={filterActivity}
        storageKey={`transfer.history.${transferId}`}
        canUndo={false}
        undoMutation={NO_UNDO}
        renderRowAction={renderNoAction}
        userNames={userNames}
        userHeadshots={profiles.data?.headshots}
        tableOptions={canViewHistory ? TABLE_OPTIONS : NO_TABLES}
        actionOptions={ACTION_OPTIONS}
        tableToTab={transferHistoryTab}
        resolveFieldLabel={transferHistoryFieldLabel}
        onFiltersChange={setFilters}
        hasMore={canViewHistory && query.hasNextPage}
        loadingMore={query.isFetchingNextPage}
        onLoadMore={loadMore}
        onRetry={error ? retry : undefined}
      />
      {!canViewHistory ? (
        <p className="transfer-record-panel__footnote">Field-by-field changes need the History permission; the activity above is what everyone sees.</p>
      ) : null}
    </div>
  );
}
